import { z } from 'zod'
import { queryBoolean } from './zod-query'

/**
 * batch-63 pauze-tweeassig (FO-473, spec 173) — gedeelde vormen voor de
 * tweeassige pauze.
 *
 * `@streep/types` mag niet van `@streep/utils` afhangen (circulair), dus de
 * string-unions staan hier nog een keer; `packages/utils` toetst in een test
 * dat ze gelijk zijn aan `PauzeVerloning`/`PauzeBron` uit `werkbon-pauze.ts`.
 * De reken- en resolutielogica staat uitsluitend in `@streep/utils`.
 */

/** Gaat de pauze van de BETAALDE uren af (GESCHREVEN) of niet (DOORBETAALD)? */
export const PAUZE_VERLONING_KEYS = ['GESCHREVEN', 'DOORBETAALD'] as const
export type PauzeVerloningKey = (typeof PAUZE_VERLONING_KEYS)[number]

/** Herkomst van een pauze-waarde, per as. */
export const PAUZE_BRON_KEYS = [
  'EXPLICIET',
  'MONTEUR',
  'CONSIGNATIE',
  'DIENST_UITZONDERING',
  'DIENST',
  'FORMULIER',
  'KORTE_DIENST',
  'KLANT_DEFAULT',
  'SYSTEEM_DEFAULT',
  'AFGELEID',
  'MIGRATIE',
] as const
export type PauzeBronKey = (typeof PAUZE_BRON_KEYS)[number]

/** Status van een werkschema in het klantoverzicht "pauze-instellingen". */
export const WERKSCHEMA_PAUZE_STATUS_KEYS = ['BEVESTIGD', 'AFGELEID', 'ONVOLLEDIG'] as const
export type WerkschemaPauzeStatusKey = (typeof WERKSCHEMA_PAUZE_STATUS_KEYS)[number]

/** Pauze-minuten op het kwartier-grid (zoals de dagregel-constraints eisen). */
const kwartierMinuten = (max: number, veld: string) =>
  z
    .number({ message: `${veld}: vul een aantal minuten in` })
    .int(`${veld}: een heel aantal minuten`)
    .min(0, `${veld} kan niet negatief zijn`)
    .max(max, `${veld} is maximaal ${max} minuten`)
    .refine((n) => n % 15 === 0, { message: `${veld}: gebruik stappen van 15 minuten` })

export const pauzeVerloningSchema = z.enum(PAUZE_VERLONING_KEYS)

/**
 * Pauze van een werkschema-dienst: heel aantal minuten 0..240, zoals vóór
 * FO-473. Bewust ZONDER kwartier-eis: een opgeslagen historische afspraak
 * (bv. 20 min) moet de PATCH van zijn eigen read-DTO overleven; de cascade
 * rondt hem voor de dagregel naar beneden af op het kwartier.
 */
const dienstPauzeMinuten = z
  .number({ message: 'Pauze: vul een aantal minuten in' })
  .int('Pauze: een heel aantal minuten')
  .min(0, 'Pauze kan niet negatief zijn')
  .max(240, 'Pauze is maximaal 240 minuten')

/**
 * Factuurpauze van een afspraak (dienst, uitzondering, klant-default): heel
 * aantal minuten 0..240, eveneens ZONDER kwartier-eis. De FO-473-migratie telt
 * de oude schema-brede facturatie-pauze op bij de dienst-pauze (bv. 20 + 30 =
 * 50); die som moet de PATCH van zijn eigen read-DTO overleven. De cascade
 * rondt hem voor de dagregel naar beneden af op het kwartier (en klemt op 240).
 */
const afspraakFactuurpauzeMinuten = z
  .number({ message: 'Factuurpauze: vul een aantal minuten in' })
  .int('Factuurpauze: een heel aantal minuten')
  .min(0, 'Factuurpauze kan niet negatief zijn')
  .max(240, 'Factuurpauze is maximaal 240 minuten')

/**
 * De drie pauze-velden van één werkschema-dienst (spec §D12). Alle drie
 * nullable: `null` = "niet ingevuld" (de standaard geldt, met zichtbare
 * herkomst); `0` = een bewuste keuze. Read↔write-consistent: de read-DTO geeft
 * na de FO-473-migratie `breakMinutes: null` terug voor onbevestigde diensten.
 */
export const werkschemaDienstPauzeVelden = {
  breakMinutes: dienstPauzeMinuten.nullable().optional(),
  pauzeVerloning: pauzeVerloningSchema.nullable().optional(),
  pauzeFacturatieMinuten: afspraakFactuurpauzeMinuten.nullable().optional(),
}

/** Idem voor een per-datum dienst-uitzondering (alleen wat ingevuld is telt). */
export const dienstUitzonderingPauzeVelden = {
  breakMinutes: dienstPauzeMinuten.optional(),
  pauzeVerloning: pauzeVerloningSchema.optional(),
  pauzeFacturatieMinuten: afspraakFactuurpauzeMinuten.optional(),
}

/** Klant-default van een werkschema op de twee andere assen. */
export const werkschemaKlantPauzeVelden = {
  standaardPauzeVerloning: pauzeVerloningSchema.nullable().optional(),
  standaardPauzeFacturatieMinuten: afspraakFactuurpauzeMinuten.nullable().optional(),
  /**
   * `true` = de pauze-afspraken van dit schema zijn gecontroleerd en bevestigd
   * (zet `pauzeBevestigdOp`); `false` = (weer) onbevestigd. Weglaten = de
   * bevestiging blijft zoals hij was.
   */
  pauzeBevestigen: z.boolean().optional(),
}

/**
 * Backoffice: handmatig afwijken per as op één dagregel (spec §B7). De portal
 * stuurt alleen `pauzeMinutes` via de bestaande dagregel-PATCH.
 *
 * `herstel` zet de gekozen as terug naar de afspraak (de cascade) en haalt de
 * handmatig-vlag van die as weg.
 */
export const dagregelPauzeAanpassingSchema = z
  .object({
    pauzeMinutes: kwartierMinuten(120, 'Pauze').optional(),
    pauzeVerloning: pauzeVerloningSchema.optional(),
    pauzeFacturatieMinuten: kwartierMinuten(240, 'Factuurpauze').optional(),
    herstel: z.enum(['VERLONING', 'FACTURATIE', 'BEIDE']).optional(),
    /**
     * Waarom kantoor afwijkt van de afspraak (komt in de werkbon-audit). FO-511
     * §5.5: bij een nieuwe factuurpauze staat deze reden ook op de bon - intern
     * én voor de klant ("pauze afwijkend: 0:15, reden ...").
     */
    reden: z
      .string({ message: 'Geef een reden voor de aanpassing' })
      .trim()
      .min(3, 'Geef een reden van minstens 3 tekens')
      .max(500, 'De reden is maximaal 500 tekens'),
  })
  .refine(
    (v) =>
      v.herstel !== undefined ||
      v.pauzeMinutes !== undefined ||
      v.pauzeVerloning !== undefined ||
      v.pauzeFacturatieMinuten !== undefined,
    { message: 'Geef minstens één pauze-waarde op, of kies "terug naar de afspraak".', path: ['pauzeMinutes'] },
  )
  .refine(
    (v) =>
      v.herstel === undefined ||
      (v.pauzeMinutes === undefined &&
        v.pauzeVerloning === undefined &&
        v.pauzeFacturatieMinuten === undefined),
    { message: 'Kies óf nieuwe waarden óf "terug naar de afspraak", niet allebei.', path: ['herstel'] },
  )
export type DagregelPauzeAanpassingInput = z.infer<typeof dagregelPauzeAanpassingSchema>

/** De pauze-velden als losse schema's, voor de client-side voorwacht in formulieren. */
export const werkschemaDienstPauzeSchema = z.object(werkschemaDienstPauzeVelden)
export const dienstUitzonderingPauzeSchema = z.object(dienstUitzonderingPauzeVelden)
export const werkschemaKlantPauzeSchema = z.object(werkschemaKlantPauzeVelden)

/**
 * Heel aantal minuten 0..240 voor de systeemstandaard, zonder kwartier-eis:
 * sinds FO-423 mocht hier elk heel getal staan, en de GET geeft dat terug
 * (read↔write). De cascade rondt het voor de dagregel af op het kwartier.
 */
const systeemPauzeMinuten = (veld: string) =>
  z
    .number({ message: `${veld}: vul een aantal minuten in` })
    .int(`${veld}: een heel aantal minuten`)
    .min(0, `${veld} kan niet negatief zijn`)
    .max(240, `${veld} is maximaal 240 minuten`)

/** Instelbare systeemdefaults (Instellingen - Uren). */
export const pauzeSysteemDefaultsSchema = z.object({
  dagdienstMinuten: systeemPauzeMinuten('Pauze dagdienst'),
  ploegenMinuten: systeemPauzeMinuten('Pauze ploeg/nacht'),
  dagdienstVerloning: pauzeVerloningSchema,
  ploegenVerloning: pauzeVerloningSchema,
})
export type PauzeSysteemDefaultsInput = z.infer<typeof pauzeSysteemDefaultsSchema>

/** De pauze-stand van één dagregel zoals een DTO hem meegeeft (beide assen). */
export interface DagregelPauzeDTO {
  /** Feitelijke pauze. */
  pauzeMinutes: number
  pauzeVerloning: PauzeVerloningKey
  /** `null` = van vóór FO-473 (factuur volgt de betaalde uren). */
  pauzeFacturatieMinuten: number | null
  pauzeBron: PauzeBronKey | null
  pauzeVerloningBron: PauzeBronKey | null
  pauzeFacturatieBron: PauzeBronKey | null
  pauzeHandmatig: boolean
  pauzeHandmatigFacturatie: boolean
  /**
   * FO-511 - de reden van een factuurpauze die kantoor afwijkend van de
   * klantregel zette (staat op de bon); `null` zolang de klantregel geldt.
   */
  pauzeFacturatieReden: string | null
  /** Facturabele uren van deze dagregel (`facturabeleUren`); `hours` = betaald. */
  facturabeleUren: number
}

/**
 * De pauze van één dagregel zoals de PORTAL hem krijgt: alleen de
 * verloningsas. De factuurpauze en de facturabele uren zijn een interne
 * klantafspraak en gaan niet naar de monteur.
 */
export type DagregelPauzeVerloningDTO = Pick<
  DagregelPauzeDTO,
  'pauzeMinutes' | 'pauzeVerloning' | 'pauzeBron' | 'pauzeVerloningBron' | 'pauzeHandmatig'
>

/**
 * Vorm van één opgeslagen dag in `FacturatieOverzichtRegel.pauzeSpecificatie`
 * (JSON). Gebruik `leesPauzeSpecificatie` om de kolom te lezen: een onbruikbare
 * dag valt weg in plaats van ongecontroleerd de DTO in te gaan.
 */
export const facturatieOverzichtPauzeDagSchema = z.object({
  datum: z.string(),
  betaaldeUren: z.number(),
  factuurUren: z.number(),
  pauzeMinuten: z.number(),
  pauzeVerloning: pauzeVerloningSchema,
  factuurPauzeMinuten: z.number(),
  pauzeBron: z.enum(PAUZE_BRON_KEYS).nullable(),
  pauzeVerloningBron: z.enum(PAUZE_BRON_KEYS).nullable(),
  pauzeFacturatieBron: z.enum(PAUZE_BRON_KEYS).nullable(),
})

/**
 * De pauze-specificatie van een overzicht-regel uit de JSON-kolom. `null` =
 * geen specificatie (regel van vóór FO-473); losse onbruikbare dagen vallen weg.
 */
export function leesPauzeSpecificatie(json: unknown): FacturatieOverzichtPauzeDagDTO[] | null {
  if (!Array.isArray(json)) return null
  const dagen: FacturatieOverzichtPauzeDagDTO[] = []
  for (const dag of json) {
    const p = facturatieOverzichtPauzeDagSchema.safeParse(dag)
    if (p.success) dagen.push(p.data)
  }
  return dagen
}

/**
 * Eén dag in de pauze-specificatie van een facturatie-overzicht-regel
 * (backoffice; NOOIT naar de klant - betaalde uren zijn intern).
 */
export interface FacturatieOverzichtPauzeDagDTO {
  /** yyyy-MM-dd */
  datum: string
  betaaldeUren: number
  factuurUren: number
  pauzeMinuten: number
  pauzeVerloning: PauzeVerloningKey
  factuurPauzeMinuten: number
  pauzeBron: PauzeBronKey | null
  pauzeVerloningBron: PauzeBronKey | null
  pauzeFacturatieBron: PauzeBronKey | null
}

/** Eén dienst in het klantoverzicht "pauze-instellingen". */
export interface PauzeInstellingDienstDTO {
  label: string
  /** "8 u dienst: betaald 8,0 u, factuur 7,5 u" (met de standaarden ingevuld). */
  uitkomst: string | null
  pauzeMinuten: number | null
  pauzeVerloning: PauzeVerloningKey | null
  pauzeFacturatieMinuten: number | null
  factuurpauzeBovenPauze: boolean
  /**
   * FO-511 - deze dienst heeft geen eigen factuurpauze en het schema geen
   * klant-standaard: bevestigen kan pas als hij is ingevuld (0 mag).
   */
  factuurpauzeOntbreekt: boolean
  /** FO-511 - het voorstel (oude schema-brede waarde of systeemstandaard). */
  factuurpauzeVoorstel: number
}

/** Eén rij in het klantoverzicht "pauze-instellingen" (spec §D13). */
export interface PauzeInstellingRijDTO {
  clientId: string
  clientNaam: string
  /** `null` = de klant heeft (nog) geen werkschema: de systeemstandaard geldt. */
  workScheduleId: string | null
  workScheduleNaam: string | null
  shiftType: string | null
  isDefault: boolean
  status: WerkschemaPauzeStatusKey
  pauzeBevestigdOp: string | null
  diensten: PauzeInstellingDienstDTO[]
  /** Klant-default op de drie assen (`null` per veld = geen afspraak). */
  standaardPauzeMinuten: number | null
  standaardPauzeVerloning: PauzeVerloningKey | null
  standaardPauzeFacturatieMinuten: number | null
  /** Ingangsdatum van de factuurpauze-afspraken (ISO) of `null` = altijd. */
  facturatiePauzeVanaf: string | null
  dubbeltelRisico: boolean
  dubbeltelMelding: string | null
  /** FO-511 - diensten zonder factuurpauze (en geen klant-standaard). */
  dienstenZonderFactuurpauze: string[]
  /**
   * FO-511 (review M3) - de open FO-423-vragen over de PAUZE (verloningsas): diensten
   * waarvan de pauze nog niet gekozen of een onbevestigde 0 is. Bulk-bevestigen slaat
   * zo'n werkschema over, tenzij de gebruiker ze bewust meebevestigt.
   */
  openPauzeVragen: string[]
}

export interface PauzeInstellingenOverzichtDTO {
  rijen: PauzeInstellingRijDTO[]
  /** Tellers over de VOLLEDIGE gefilterde set (niet over de pagina). */
  aantallen: Record<WerkschemaPauzeStatusKey, number>
}

export const pauzeInstellingenQuerySchema = z.object({
  status: z.enum(WERKSCHEMA_PAUZE_STATUS_KEYS).optional(),
  /**
   * FO-511 - alleen de pauzeregels die nog te bevestigen zijn (status niet
   * BEVESTIGD, met werkschema). `queryBoolean`: "false" betekent echt uit.
   */
  teBevestigen: queryBoolean().optional(),
  search: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type PauzeInstellingenQuery = z.infer<typeof pauzeInstellingenQuerySchema>

/** Antwoord van de per-as-aanpassing: de nieuwe stand van de dagregel. */
export interface DagregelPauzeAanpassingResultaatDTO {
  dayEntryId: string
  hours: number
  pauze: DagregelPauzeDTO
  /**
   * "Terug naar de afspraak" kwam uit het huidige rooster van de klant, omdat
   * het rooster van de werkbon verwijderd is (review batch-63).
   */
  werkschemaVervangen?: boolean
}
